import React from 'react';
import { createRoot } from 'react-dom/client';
import { createPortal } from 'react-dom';
import './toast.scss';

export const createToast = () => {
  let dispatchRef = null;
  let root = null;
  let hostEl = null;
  const mounts = {};
  const earlyQueue = [];
  let id = 1;

  const genId = () => ++id;

  const ensureHostMounted = () => {
    if (!hostEl || !hostEl.isConnected) {
      hostEl =
        document.querySelector('[data-toast-host="true"]') ||
        (() => {
          const el = document.createElement('div');
          el.setAttribute('data-toast-host', 'true');
          document.body.appendChild(el);
          return el;
        })();
    }
    if (!root) {
      root = createRoot(hostEl);
      root.render(<Host />);
    }
  };

  const getOrCreateMount = pos => {
    let el = mounts[pos];
    if (!el || !el.isConnected) {
      el = document.querySelector(`[data-toast-position="${pos}"]`);
      if (!el) {
        el = document.createElement('div');
        el.setAttribute('data-toast-position', pos);
        el.className = `toast-stack ${posToClass(pos)}`;
        document.body.appendChild(el);
      } else {
        el.classList.add('toast-stack', posToClass(pos));
      }
      mounts[pos] = el;
    }
    return el;
  };

  const posToClass = pos => {
    switch (pos) {
      case 'top-left':
        return 'toast-pos-top-left';
      case 'top-right':
        return 'toast-pos-top-right';
      case 'top-center':
        return 'toast-pos-top-center';
      case 'bottom-left':
        return 'toast-pos-bottom-left';
      case 'bottom-right':
        return 'toast-pos-bottom-right';
      case 'bottom-center':
        return 'toast-pos-bottom-center';
      default:
        return 'toast-pos-top-right';
    }
  };

  const resolveThemeClass = variant => {
    if (variant === 'success') return 'toast-theme-success';
    if (variant === 'error') return 'toast-theme-error';
    if (variant === 'info') return 'toast-theme-info';
    return 'toast-theme-default';
  };

  function Host() {
    const [state, dispatch] = React.useReducer(reducer, { toasts: [] });
    React.useEffect(() => {
      dispatchRef = dispatch;
      while (earlyQueue.length) dispatch(earlyQueue.shift());
      return () => {
        if (dispatchRef === dispatch) dispatchRef = null;
      };
    }, []);
    const grouped = React.useMemo(
      () => groupByPosition(state.toasts),
      [state.toasts]
    );
    return (
      <>
        {Object.keys(grouped).map(pos =>
          createPortal(
            <Stack key={pos} items={grouped[pos]} />,
            getOrCreateMount(pos)
          )
        )}
      </>
    );
  }

  function reducer(state, action) {
    if (action.type === 'ADD') {
      const {
        id,
        message = '',
        position = 'bottom-left',
        duration = 5000,
        variant,
        className,
        showIcon = false,
        link,
        content,
        render,
        dismissible = true
      } = action.payload || {};
      const t = {
        id: id ?? genId(),
        message: String(message),
        position,
        duration,
        themeClass: resolveThemeClass(variant),
        className: className || '',
        showIcon,
        link: link && typeof link === 'object' ? link : null,
        content: content ?? null,
        render: typeof render === 'function' ? render : null,
        dismissible,
        createdAt: Date.now()
      };
      if (duration > 0) {
        window.setTimeout(() => {
          if (dispatchRef)
            dispatchRef({ type: 'DISMISS', payload: { id: t.id } });
        }, duration);
      }
      return { ...state, toasts: [t, ...state.toasts].slice(0, 5) };
    }
    if (action.type === 'DISMISS') {
      const id = action.payload?.id;
      return { ...state, toasts: state.toasts.filter(t => t.id !== id) };
    }
    if (action.type === 'CLEAR') {
      return { ...state, toasts: [] };
    }
    return state;
  }

  const groupByPosition = list => {
    const g = {};
    for (let i = 0; i < list.length; i++) {
      const t = list[i];
      (g[t.position] || (g[t.position] = [])).push(t);
    }
    return g;
  };

  function Stack({ items }) {
    return (
      <>
        {items.map(t => (
          <Item key={t.id} {...t} />
        ))}
      </>
    );
  }

  function Item({
    id,
    message,
    content,
    render,
    themeClass,
    className,
    showIcon,
    link,
    dismissible
  }) {
    const helpers = React.useMemo(
      () => ({ dismiss: () => api.dismiss(id), id }),
      [id]
    );
    const body = render ? (
      render(helpers)
    ) : content != null ? (
      content
    ) : (
      <div className="toast-item__body">{message}</div>
    );
    const onLinkClick = e => {
      if (!link) return;
      if (typeof link.onClick === 'function') {
        e.preventDefault();
        link.onClick(e);
      }
    };
    return (
      <div
        role="status"
        aria-live="polite"
        className={`toast-item ${themeClass} ${className || ''}`}
      >
        {showIcon ? (
          <span className="toast-item__icon">
            <img
              src="/content/images/icons/toast-CheckMark.svg"
              height="24"
              width="24"
              alt=""
            />
          </span>
        ) : null}
        <div className="toast-item__body">{body}</div>
        {link ? (
          <a
            className="toast-item__link"
            href={link.href}
            onClick={onLinkClick}
          >
            {link.label}
          </a>
        ) : null}
        {dismissible ? (
          <button
            className="toast-item__close"
            aria-label="Dismiss"
            onClick={() => api.dismiss(id)}
          >
            ×
          </button>
        ) : null}
      </div>
    );
  }

  const api = {
    show(opts = {}) {
      ensureHostMounted();
      const add = { type: 'ADD', payload: opts };
      if (dispatchRef) {
        dispatchRef(add);
        return;
      }
      earlyQueue.push(add);
      setTimeout(() => {
        ensureHostMounted();
        if (dispatchRef)
          while (earlyQueue.length) dispatchRef(earlyQueue.shift());
      }, 0);
    },
    dismiss(id) {
      ensureHostMounted();
      const act = { type: 'DISMISS', payload: { id } };
      if (dispatchRef) dispatchRef(act);
      else earlyQueue.push(act);
    },
    clear() {
      ensureHostMounted();
      const act = { type: 'CLEAR' };
      if (dispatchRef) dispatchRef(act);
      else earlyQueue.push(act);
    },
    success(message, opts = {}) {
      this.show({ message, variant: 'success', ...opts });
    },
    error(message, opts = {}) {
      this.show({ message, variant: 'error', ...opts });
    },
    info(message, opts = {}) {
      this.show({ message, variant: 'info', ...opts });
    }
  };

  return api;
};

const toast = createToast();
export default toast;
